import { useState } from 'react';
import { Tag, Modal, Form, Input, InputNumber, Select, DatePicker, message } from 'antd';
import { ResourceList } from '@/components/ResourceList';
import { listCoupons, createCoupon, type Coupon, type CreateCouponInput } from '@/api/coupons';
import { formatDateTime } from '@/utils/format';

const KIND_OPTIONS = [
  { value: 'discount', label: '折扣 (value = 立减分)' },
  { value: 'free_trial', label: '免费体验 (value = 0, 整单免)' },
  { value: 'diamond_gift', label: '送钻石 (value = 钻石数)' },
];
const STATUS_OPTIONS = [
  { value: 'active', label: 'active' },
  { value: 'paused', label: 'paused' },
  { value: 'archived', label: 'archived' },
];

export function CouponsListPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async () => {
    const v = await form.validateFields();
    let conditions: unknown;
    if (v.conditions) {
      try {
        conditions = JSON.parse(v.conditions);
      } catch {
        message.error('conditions 不是合法 JSON');
        return;
      }
    }
    setSubmitting(true);
    try {
      const input: CreateCouponInput = {
        code: v.code,
        name: v.name,
        kind: v.kind,
        value: v.value ?? 0,
        max_per_user: v.max_per_user ?? 1,
        max_total_uses: v.max_total_uses ?? null,
        app_id: v.app_id ?? null,
        status: v.status ?? 'active',
        valid_until: v.valid_until ? v.valid_until.toISOString() : null,
        conditions,
      };
      await createCoupon(input);
      message.success('优惠券已创建');
      setOpen(false);
      form.resetFields();
      setReloadKey((k) => k + 1);
    } catch (e) {
      message.error((e as Error).message || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ResourceList<Coupon>
        key={reloadKey}
        title="优惠券"
        description="券模板 (app_id 空 = 平台级跨 App). 创建后由新人自动发放或「发券」绑定给用户."
        rowKey="id"
        pagination="offset"
        actions={[{ label: '新建优惠券', type: 'primary', onClick: () => setOpen(true) }]}
        filters={[{ key: 'app_id', label: 'App ID', type: 'text', placeholder: '数字, 空=全部' }]}
        fetcher={({ filters, page, pageSize }) => {
          const q: { app_id?: number; limit: number; offset: number } = {
            limit: pageSize,
            offset: (page - 1) * pageSize,
          };
          if (typeof filters.app_id === 'string' && filters.app_id) q.app_id = Number(filters.app_id);
          return listCoupons(q);
        }}
        columns={[
          { title: 'ID', dataIndex: 'id' },
          { title: 'Code', dataIndex: 'code', render: (v) => <Tag>{v}</Tag> },
          { title: '名称', dataIndex: 'name' },
          { title: '类型', dataIndex: 'kind', render: (v) => <Tag color="blue">{v}</Tag> },
          { title: 'value', dataIndex: 'value', align: 'right' },
          {
            title: 'App',
            dataIndex: 'app_id',
            render: (v: number | null) => (v ? `app#${v}` : <Tag>平台级</Tag>),
          },
          { title: '每人限', dataIndex: 'max_per_user', align: 'right' },
          {
            title: '状态',
            dataIndex: 'status',
            render: (v: string) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>,
          },
          {
            title: '有效期至',
            dataIndex: 'valid_until',
            render: (v: string | null) => (v ? formatDateTime(v) : '永久'),
          },
          { title: '创建', dataIndex: 'created_at', render: (v: string) => formatDateTime(v) },
        ]}
      />
      <Modal
        title="新建优惠券"
        open={open}
        onOk={handleCreate}
        confirmLoading={submitting}
        onCancel={() => setOpen(false)}
        okText="创建"
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" initialValues={{ kind: 'discount', status: 'active', max_per_user: 1 }}>
          <Form.Item name="code" label="Code (唯一码)" rules={[{ required: true }]}>
            <Input placeholder="如 NEWUSER10 / FREETRIAL" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="运营展示名" />
          </Form.Item>
          <Form.Item name="kind" label="类型" rules={[{ required: true }]}>
            <Select options={KIND_OPTIONS} />
          </Form.Item>
          <Form.Item name="value" label="value (随类型: 立减分 / 0 / 钻石数)" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="app_id" label="App ID (空 = 平台级跨 App)">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="max_per_user" label="每人最多用">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="max_total_uses" label="总量上限 (空 = 不限)">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="valid_until" label="有效期至 (空 = 永久)">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="conditions"
            label="conditions (JSON, 可选)"
            tooltip='如 {"applies_to_sku_kinds":["one_time"],"first_purchase_only":true}'
          >
            <Input.TextArea rows={2} placeholder='{"first_purchase_only": true}' />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
